/**
 * Chat Session REST API 客户端
 *
 * 提供聊天会话的 CRUD 操作，替代 WebSocket 进行会话管理。
 */

import type { ChatSession, ChatMessage, ChatSessionCreate } from '../types/chat';

const API_BASE = '/api/chat/sessions';

interface SessionListResponse {
  sessions: ChatSession[];
  total: number;
}

interface SessionGetResponse {
  session: ChatSession;
  messages: ChatMessage[];
  total: number;
}

interface ExportResponse {
  content: string;
  filename: string;
}

interface ImportResponse {
  success: boolean;
  imported_count: number;
  skipped_count: number;
  error_count: number;
  errors?: string[];
}

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: '请求失败' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  return response.json();
}

export const chatSessionApi = {
  async listSessions(
    workspaceId: number,
    branchId?: number | null,
    page: number = 1,
    limit: number = 20
  ): Promise<SessionListResponse> {
    const params = new URLSearchParams({
      workspace_id: String(workspaceId),
      page: String(page),
      limit: String(limit),
    });
    if (branchId !== undefined && branchId !== null) {
      params.append('branch_id', String(branchId));
    }

    const response = await fetch(`${API_BASE}?${params}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    return handleResponse<SessionListResponse>(response);
  },

  async createSession(data: ChatSessionCreate): Promise<ChatSession> {
    const response = await fetch(API_BASE, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    return handleResponse<ChatSession>(response);
  },

  async getSession(sessionId: string, workspaceId: number): Promise<SessionGetResponse> {
    const params = new URLSearchParams({
      workspace_id: String(workspaceId),
    });

    const response = await fetch(`${API_BASE}/${sessionId}?${params}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    return handleResponse<SessionGetResponse>(response);
  },

  async deleteSession(sessionId: string, workspaceId: number): Promise<void> {
    const params = new URLSearchParams({
      workspace_id: String(workspaceId),
    });

    const response = await fetch(`${API_BASE}/${sessionId}?${params}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    await handleResponse(response);
  },

  async switchModel(
    sessionId: string,
    workspaceId: number,
    model: string
  ): Promise<ChatSession> {
    const params = new URLSearchParams({
      workspace_id: String(workspaceId),
    });

    const response = await fetch(`${API_BASE}/${sessionId}/model?${params}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ model }),
    });

    return handleResponse<ChatSession>(response);
  },

  async rollbackMessage(
    sessionId: string,
    workspaceId: number,
    messageId?: number
  ): Promise<{ success: boolean; result: unknown }> {
    const params = new URLSearchParams({
      workspace_id: String(workspaceId),
    });

    const response = await fetch(`${API_BASE}/${sessionId}/rollback?${params}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ message_id: messageId }),
    });

    return handleResponse(response);
  },

  async exportSession(sessionId: string, workspaceId: number): Promise<ExportResponse> {
    const params = new URLSearchParams({
      workspace_id: String(workspaceId),
    });

    const response = await fetch(`${API_BASE}/${sessionId}/export?${params}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    return handleResponse<ExportResponse>(response);
  },

  async exportSessions(
    sessionIds: string[],
    workspaceId: number
  ): Promise<ExportResponse> {
    const params = new URLSearchParams({
      workspace_id: String(workspaceId),
    });

    const response = await fetch(`${API_BASE}/export?${params}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ session_ids: sessionIds }),
    });

    return handleResponse<ExportResponse>(response);
  },

  async exportAllSessions(workspaceId: number): Promise<ExportResponse> {
    const params = new URLSearchParams({
      workspace_id: String(workspaceId),
    });

    const response = await fetch(`${API_BASE}/export-all?${params}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    return handleResponse<ExportResponse>(response);
  },

  async importSessions(
    content: string,
    workspaceId: number,
    conflictResolution: 'skip' | 'overwrite' | 'rename' = 'skip'
  ): Promise<ImportResponse> {
    const params = new URLSearchParams({
      workspace_id: String(workspaceId),
    });

    const response = await fetch(`${API_BASE}/import?${params}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        content,
        conflict_resolution: conflictResolution,
      }),
    });

    return handleResponse<ImportResponse>(response);
  },

  async previewImport(
    content: string,
    workspaceId: number
  ): Promise<{ sessions: Array<{ title: string; message_count: number }>; total: number }> {
    const params = new URLSearchParams({
      workspace_id: String(workspaceId),
    });

    const response = await fetch(`${API_BASE}/import/preview?${params}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ content }),
    });

    return handleResponse(response);
  },

  async replyQuestion(
    questionId: string,
    answers: string[][]
  ): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE}/questions/${questionId}/reply`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ question_id: questionId, answers }),
    });

    return handleResponse(response);
  },

  async dismissQuestion(questionId: string): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE}/questions/${questionId}/dismiss`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    return handleResponse(response);
  },

  /** 中止会话正在生成的消息（后端通过中止标志停止 agent） */
  async abortSessionMessage(sessionId: string): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE}/${sessionId}/abort`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    return handleResponse(response);
  },
};

export default chatSessionApi;